import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parse } from "date-fns";
import { de } from "date-fns/locale";
import { 
  Car, 
  Calendar, 
  Mail, 
  LogOut, 
  LayoutTemplate,
  Plus, 
  Pencil, 
  Trash2,
  Clock,
  Phone,
  X,
  Check,
  XCircle,
  Send,
  ShoppingCart,
  Upload,
  Image,
  Settings,
  Printer,
  FileText,
  Star,
  ThumbsUp,
  MessageSquare,
  Search,
  Info,
  Gauge,
  Zap,
  FileUp,
  Download,
  Eye,
  Tag,
  Sparkles,
  ToggleLeft,
  ToggleRight,
  CalendarRange,
  CalendarIcon,
  Bell,
  AlertTriangle,
  Settings2,
  Layers,
  CircleDot,
  Users
} from "lucide-react";
import PurchaseContract from "@/components/PurchaseContract";
import { useReactToPrint } from "react-to-print";
import VehicleXMLDropzone from "@/components/VehicleXMLDropzone";
import { ContactStatusControl } from "@/components/ContactStatusControl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    brand: "Renault",
    model: "",
    year: new Date().getFullYear(),
    price: "",
    mileage: "",
    fuel_type: "Benzin",
    transmission: "Manuell",
    color: "",
    image_url: "",
    is_new: false,
    features: [],
    // Extended DAT fields
    vin: "",
    hsn: "",
    tsn: "",
    engine_power_kw: "",
    engine_power_ps: "",
    engine_displacement: "",
    doors: "",
    seats: "",
    emission_class: "",
    co2_emission: "",
    fuel_consumption: "",
    first_registration: "",
    previous_owners: "",
    next_inspection: "",
    vehicle_type: "",
    body_type: "",
    drive_type: "",
    weight: "",
    license_plate: "",
    key_count: 2,
    boersentyp: ""
  });
  const [featureInput, setFeatureInput] = useState("");
  const [vinLookupLoading, setVinLookupLoading] = useState(false);
  const [showXMLDropzone, setShowXMLDropzone] = useState(false);
  
  // Status dialog state
  const [statusDialog, setStatusDialog] = useState(false);
  const [statusAppointment, setStatusAppointment] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);
  
  // Edit appointment dialog
  const [editAppointmentDialog, setEditAppointmentDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    preferred_date: "",
    preferred_time: "",
    service_type: ""
  });
  
  // Contact detail dialog
  const [contactDetailDialog, setContactDetailDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Settings state
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  
  // Feature toggles state
  const [featureSettings, setFeatureSettings] = useState({
    feature_fahrzeuge: true,
    feature_aktionen: true,
    feature_termine: true,
    feature_bewertungen: true,
    feature_kontakt: true,
    feature_reifenlager: true,
    feature_kundenportal: true
  });
  const [savingFeatures, setSavingFeatures] = useState(false);
  
  // Vehicle image upload state
  const [vehicleImageUploading, setVehicleImageUploading] = useState(false);
  
  // Purchase contract state
  const [contractDialog, setContractDialog] = useState(false);
  const [contractVehicle, setContractVehicle] = useState(null);
  const [contractBuyer, setContractBuyer] = useState({
    name: "",
    address: "",
    city: "",
    phone: "",
    email: "",
    idNumber: ""
  });
  const [contractOptions, setContractOptions] = useState({
    // Warranty
    warranty12Months: false,
    warranty24Months: false,
    // Inspection
    newInspection: false,
    // Extras
    winterTires: false,
    winterTiresPrice: 0,
    accessories: false,
    accessoriesPrice: 0,
    accessoriesDescription: "",
    // Trade-in
    tradeIn: false,
    tradeInValue: 0,
    tradeInVehicle: "",
    // Payment
    paymentCash: true,
    paymentTransfer: false,
    paymentFinancing: false,
    financingBank: "",
    deliveryDate: "",
    // Vehicle condition
    testDriveYes: true,
    testDriveNo: false,
    accidentFree: true,
    accidentRepaired: false,
    accidentUnknown: false,
    knownDefects: "",
    // Buyer type
    buyerPrivate: true,
    buyerBusiness: false,
    // Included items
    includeServiceBook: true,
    includeManual: true,
    includeSpareWheel: true,
    includeFirstAid: true,
    includeWarningTriangle: true,
    includeVest: true,
    includeWinterTires: false,
    includeRoofRack: false,
    includeFloorMats: true,
    // Additional
    additionalAgreements: ""
  });
  const contractRef = useRef(null);
  
  // Review response state
  const [reviewResponseDialog, setReviewResponseDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewResponse, setReviewResponse] = useState("");

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [selectedTicketCategory, setSelectedTicketCategory] = useState("alle");
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Aktionen state
  const [aktionen, setAktionen] = useState([]);
  const [aktionenLoading, setAktionenLoading] = useState(false);
  const [aktionDialog, setAktionDialog] = useState(false);
  const [editingAktion, setEditingAktion] = useState(null);
  const [aktionForm, setAktionForm] = useState({
    titel: "",
    beschreibung: "",
    kurzbeschreibung: "",
    bild_url: "",
    start_datum: "",
    end_datum: "",
    ist_aktiv: true,
    auto_aktivierung: false,
    wiederholung: "none",
    prioritaet: 0
  });
  const [aktionImageGenerating, setAktionImageGenerating] = useState(false);
  const [imagePreviewDialog, setImagePreviewDialog] = useState(false);
  const [imageVarianten, setImageVarianten] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Erinnerungen state
  const [reminderStatus, setReminderStatus] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  // Confirm dialog state (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    onConfirm: null
  });

  const showConfirmDialog = (title, description, onConfirm) => {
    setConfirmDialog({ open: true, title, description, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, title: "", description: "", onConfirm: null });
  };

  const token = localStorage.getItem("adminToken");

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    verifyAndLoad();
    const iv = setInterval(() => {
      axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } }).catch((err) => {
        if (err.response?.status === 401) {
          clearInterval(iv);
          localStorage.removeItem("adminToken");
          sessionStorage.removeItem("usbAdminToken");
          toast.info("Abgemeldet - USB-Schlüssel wurde entfernt oder Sitzung beendet");
          navigate("/admin");
        }
      });
    }, 10000);
    return () => clearInterval(iv);
  }, [token, navigate]);

  const verifyAndLoad = async () => {
    try {
      await axios.get(`${API}/admin/verify`, authHeaders);
      loadData();
    } catch (error) {
      localStorage.removeItem("adminToken");
      navigate("/admin");
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, appointmentsRes, contactsRes, settingsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/appointments`, authHeaders),
        axios.get(`${API}/contacts`, authHeaders),
        axios.get(`${API}/settings`),
        axios.get(`${API}/reviews/all`, authHeaders)
      ]);
      setVehicles(vehiclesRes.data);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
      setReviews(reviewsRes.data);
      if (settingsRes.data.hero_image_url) {
        setHeroImageUrl(settingsRes.data.hero_image_url);
      }
      // Load feature settings
      setFeatureSettings({
        feature_fahrzeuge: settingsRes.data.feature_fahrzeuge ?? true,
        feature_aktionen: settingsRes.data.feature_aktionen ?? true,
        feature_termine: settingsRes.data.feature_termine ?? true,
        feature_bewertungen: settingsRes.data.feature_bewertungen ?? true,
        feature_kontakt: settingsRes.data.feature_kontakt ?? true,
        feature_reifenlager: settingsRes.data.feature_reifenlager ?? true,
        feature_kundenportal: settingsRes.data.feature_kundenportal ?? true
      });
      
      // Load tickets
      loadTickets();
      // Load aktionen
      loadAktionen();
      // Load reminder status
      loadReminderStatus();
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const [ticketsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/tickets${selectedTicketCategory !== "alle" ? `?kategorie=${selectedTicketCategory}` : ""}`, authHeaders),
        axios.get(`${API}/tickets/kategorien`, authHeaders)
      ]);
      setTickets(ticketsRes.data);
      setTicketCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error loading tickets:", error);
    } finally {
      setTicketsLoading(false);
    }
  };

  // ========== AKTIONEN FUNCTIONS ==========
  const loadAktionen = async () => {
    setAktionenLoading(true);
    try {
      const response = await axios.get(`${API}/aktionen`);
      setAktionen(response.data);
    } catch (error) {
      console.error("Error loading aktionen:", error);
    } finally {
      setAktionenLoading(false);
    }
  };

  const loadReminderStatus = async () => {
    setReminderLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reminder-status`, authHeaders);
      setReminderStatus(response.data);
    } catch (error) {
      console.error("Error loading reminder status:", error);
    } finally {
      setReminderLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {}, authHeaders);
      toast.success(`Erinnerungen geprüft: ${response.data.emails_sent} E-Mails gesendet`);
      loadReminderStatus();
    } catch (error) {
      toast.error("Fehler beim Senden der Erinnerungen");
      console.error(error);
    } finally {
      setSendingReminders(false);
    }
  };

  const handleFeatureToggle = async (featureName, value) => {
    const newSettings = { ...featureSettings, [featureName]: value };
    setFeatureSettings(newSettings);
    
    setSavingFeatures(true);
    try {
      await axios.put(`${API}/settings`, {
        hero_image_url: heroImageUrl,
        ...newSettings
      }, authHeaders);
      toast.success(`${featureName.replace('feature_', '').charAt(0).toUpperCase() + featureName.replace('feature_', '').slice(1)} ${value ? 'aktiviert' : 'deaktiviert'}`);
    } catch (error) {
      console.error("Error saving feature settings:", error);
      toast.error("Fehler beim Speichern");
      // Revert on error
      setFeatureSettings(featureSettings);
    } finally {
      setSavingFeatures(false);
    }
  };

  const resetAktionForm = () => {
    setAktionForm({
      titel: "",
      beschreibung: "",
      kurzbeschreibung: "",
      bild_url: "",
      start_datum: "",
      end_datum: "",
      ist_aktiv: true,
      auto_aktivierung: false,
      wiederholung: "none",
      prioritaet: 0
    });
    setEditingAktion(null);
  };

  const handleAktionSubmit = async () => {
    if (!aktionForm.titel || !aktionForm.start_datum || !aktionForm.end_datum) {
      toast.error("Bitte Titel, Start- und Enddatum ausfüllen");
      return;
    }

    try {
      if (editingAktion) {
        await axios.put(`${API}/aktionen/${editingAktion.id}`, aktionForm, authHeaders);
        toast.success("Aktion aktualisiert");
      } else {
        await axios.post(`${API}/aktionen`, aktionForm, authHeaders);
        toast.success("Aktion erstellt");
      }
      setAktionDialog(false);
      resetAktionForm();
      loadAktionen();
    } catch (error) {
      console.error("Error saving aktion:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const editAktion = (aktion) => {
    setEditingAktion(aktion);
    setAktionForm({
      titel: aktion.titel || "",
      beschreibung: aktion.beschreibung || "",
      kurzbeschreibung: aktion.kurzbeschreibung || "",
      bild_url: aktion.bild_url || "",
      start_datum: aktion.start_datum?.split("T")[0] || "",
      end_datum: aktion.end_datum?.split("T")[0] || "",
      ist_aktiv: aktion.ist_aktiv ?? true,
      auto_aktivierung: aktion.auto_aktivierung ?? false,
      wiederholung: aktion.wiederholung || "none",
      prioritaet: aktion.prioritaet || 0
    });
    setAktionDialog(true);
  };

  const toggleAktion = async (aktionId) => {
    try {
      await axios.patch(`${API}/aktionen/${aktionId}/toggle`, {}, authHeaders);
      loadAktionen();
      toast.success("Status geändert");
    } catch (error) {
      toast.error("Fehler beim Ändern des Status");
    }
  };

  const deleteAktion = async (aktionId) => {
    showConfirmDialog(
      "Aktion löschen",
      "Möchten Sie diese Aktion wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
      async () => {
        try {
          await axios.delete(`${API}/aktionen/${aktionId}`, authHeaders);
          toast.success("Aktion gelöscht");
          loadAktionen();
        } catch (error) {
          toast.error("Fehler beim Löschen");
        }
        closeConfirmDialog();
      }
    );
  };

  const handleAktionImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const url = await handleImageUpload(file, "aktion");
      if (url) {
        setAktionForm(prev => ({ ...prev, bild_url: url }));
        toast.success("Bild hochgeladen");
      }
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    }
  };

  const generateAktionImage = async () => {
    if (!aktionForm.titel) {
      toast.error("Bitte zuerst einen Titel eingeben");
      return;
    }
    
    setAktionImageGenerating(true);
    setImageVarianten([]);
    toast.info("3 Bildvarianten werden generiert... Dies kann bis zu 2 Minuten dauern.", { duration: 10000 });
    
    try {
      const response = await axios.post(`${API}/aktionen/generate-image`, {
        titel: aktionForm.titel,
        beschreibung: aktionForm.beschreibung
      }, { 
        ...authHeaders,
        timeout: 180000  // 3 minute timeout for 3 images
      });
      
      if (response.data.varianten && response.data.varianten.length > 0) {
        // Konvertiere relative URLs zu vollständigen URLs
        const fullUrls = response.data.varianten.map(url => 
          url.startsWith('http') ? url : `${BACKEND_URL}${url}`
        );
        setImageVarianten(fullUrls);
        setSelectedImageIndex(0);
        setImagePreviewDialog(true);
        toast.success(`${fullUrls.length} Bildvarianten generiert! Wählen Sie Ihr Favorit.`);
      } else if (response.data.bild_url) {
        // Fallback für einzelnes Bild
        const fullUrl = response.data.bild_url.startsWith('http') 
          ? response.data.bild_url 
          : `${BACKEND_URL}${response.data.bild_url}`;
        setImageVarianten([fullUrl]);
        setSelectedImageIndex(0);
        setImagePreviewDialog(true);
        toast.success("Bild generiert!");
      } else {
        toast.info(response.data.hinweis || "KI-Bildgenerierung wird vorbereitet");
      }
    } catch (error) {
      console.error("AI image generation error:", error);
      toast.error("Fehler bei der KI-Bildgenerierung. Bitte versuchen Sie es erneut.");
    } finally {
      setAktionImageGenerating(false);
    }
  };

  const selectImageVariant = (index) => {
    setSelectedImageIndex(index);
  };

  const confirmImageSelection = () => {
    if (imageVarianten[selectedImageIndex]) {
      setAktionForm(prev => ({ ...prev, bild_url: imageVarianten[selectedImageIndex] }));
      setImagePreviewDialog(false);
      toast.success("Bild ausgewählt!");
    }
  };

  const formatDateDE = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  const wiederholungLabels = {
    none: "Keine",
    daily: "Täglich",
    weekly: "Wöchentlich",
    monthly: "Monatlich",
    yearly: "Jährlich",
    custom: "Benutzerdefiniert"
  };

  const exportTickets = async () => {
    try {
      const response = await axios.get(
        `${API}/tickets/export${selectedTicketCategory !== "alle" ? `?kategorie=${selectedTicketCategory}` : ""}`,
        {
          ...authHeaders,
          responseType: 'blob'
        }
      );
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tickets_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Tickets exportiert");
    } catch (error) {
      toast.error("Fehler beim Export");
    }
  };

  const downloadSingleTicket = async (ticketId, ticketNr) => {
    try {
      const response = await axios.get(
        `${API}/tickets/${ticketId}/download`,
        {
          ...authHeaders,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticketNr}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Ticket heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const deleteTicket = async (ticketId) => {
    showConfirmDialog(
      "Ticket löschen",
      "Möchten Sie dieses Ticket wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
      async () => {
        try {
          await axios.delete(`${API}/tickets/${ticketId}`, authHeaders);
          toast.success("Ticket gelöscht");
          loadTickets();
        } catch (error) {
          toast.error("Fehler beim Löschen");
        }
        closeConfirmDialog();
      }
    );
  };

  // Image upload handler
  const handleImageUpload = async (file, imageType = "general") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("image_type", imageType);
    
    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          ...authHeaders.headers,
          "Content-Type": "multipart/form-data"
        }
      });
      return `${BACKEND_URL}${response.data.url}`;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Bild-Upload fehlgeschlagen");
      return null;
    }
  };

  // Hero image upload
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setHeroImageUploading(true);
    try {
      const imageUrl = await handleImageUpload(file, "hero");
      if (imageUrl) {
        setHeroImageUrl(imageUrl);
        // Save to settings
        await axios.put(`${API}/settings`, { hero_image_url: imageUrl }, authHeaders);
        toast.success("Startseiten-Hintergrund aktualisiert!");
      }
    } finally {
      setHeroImageUploading(false);
    }
  };

  // Vehicle image upload
  const handleVehicleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setVehicleImageUploading(true);
    try {
      const imageUrl = await handleImageUpload(file, "vehicle");
      if (imageUrl) {
        setVehicleForm(prev => ({ ...prev, image_url: imageUrl }));
        toast.success("Fahrzeugbild hochgeladen!");
      }
    } finally {
      setVehicleImageUploading(false);
    }
  };

  // VIN Lookup function
  const handleVinLookup = async () => {
    const vin = vehicleForm.vin?.trim();
    if (!vin || vin.length !== 17) {
      toast.error("Bitte geben Sie eine gültige 17-stellige FIN ein");
      return;
    }

    setVinLookupLoading(true);
    try {
      const response = await axios.get(`${API}/vin-lookup/${vin}`, authHeaders);
      const result = response.data;

      if (result.success && result.data) {
        const data = result.data;
        
        // Check if this is from database (has existing images)
        const isFromDatabase = result.source === "database";
        
        // Update form with found data
        setVehicleForm(prev => ({
          ...prev,
          brand: data.brand || prev.brand,
          model: data.model || prev.model,
          year: data.year || prev.year,
          hsn: data.hsn || prev.hsn,
          tsn: data.tsn || prev.tsn,
          vehicle_type: data.vehicle_type || prev.vehicle_type,
          body_type: data.body_type || prev.body_type,
          doors: data.doors || prev.doors,
          seats: data.seats || prev.seats,
          engine_displacement: data.engine_displacement || prev.engine_displacement,
          engine_power_kw: data.engine_power_kw || data.power_kw || prev.engine_power_kw,
          engine_power_ps: data.engine_power_ps || (data.engine_power_kw ? Math.round(data.engine_power_kw * 1.36) : (data.power_kw ? Math.round(data.power_kw * 1.36) : prev.engine_power_ps)),
          fuel_type: data.fuel_type || data.fuel || prev.fuel_type,
          transmission: data.transmission || prev.transmission,
          drive_type: data.drive_type || prev.drive_type,
          emission_class: data.emission_class || data.emissions_class || prev.emission_class,
          co2_emission: data.co2_emission || data.co2_emissions || prev.co2_emission,
          fuel_consumption: data.fuel_consumption || prev.fuel_consumption,
          weight: data.weight || prev.weight,
          features: data.features?.length ? data.features : prev.features,
          mileage: data.mileage || prev.mileage,
          price: data.price || prev.price,
          color: data.color || prev.color,
          boersentyp: data.boersentyp || prev.boersentyp,
          // Load image from database if available
          image_url: isFromDatabase && data.image_url ? data.image_url : prev.image_url
        }));

        if (isFromDatabase) {
          toast.success(`Fahrzeug aus Datenbank geladen: ${data.brand} ${data.model} (inkl. Bilder)`);
        } else {
          toast.success(`Fahrzeugdaten von ${result.source.toUpperCase()} geladen: ${data.brand} ${data.model}`);
        }
      } else {
        toast.error(result.error || "Keine Daten gefunden");
      }
    } catch (error) {
      console.error("VIN lookup error:", error);
      toast.error("Fehler bei der FIN-Abfrage");
    } finally {
      setVinLookupLoading(false);
    }
  };

  // Handle XML vehicle data import
  const handleXMLVehicleData = (data) => {
    setVehicleForm(prev => ({
      ...prev,
      brand: data.brand || prev.brand,
      model: data.model || prev.model,
      year: data.year || prev.year,
      mileage: data.mileage || prev.mileage,
      color: data.color || prev.color,
      fuel_type: data.fuel || prev.fuel_type,
      transmission: data.transmission || prev.transmission,
      vin: data.vin || prev.vin,
      engine_power_kw: data.power ? parseInt(data.power) : prev.engine_power_kw,
      engine_power_ps: data.power ? Math.round(parseInt(data.power) * 1.36) : prev.engine_power_ps,
      features: data.features?.length ? data.features : prev.features,
      boersentyp: data.description || data.boersentyp || prev.boersentyp,
      vehicle_type: data.vehicleType || prev.vehicle_type,
      license_plate: data.licensePlate || prev.license_plate,
      hsn: data.kbaCode?.split("/")[0] || prev.hsn,
      tsn: data.kbaCode?.split("/")[1] || prev.tsn,
    }));
    
    setShowXMLDropzone(false);
    setVehicleDialog(true);
    
    // Detaillierte Erfolgsmeldung
    const details = [];
    if (data.brand) details.push(data.brand);
    if (data.model) details.push(data.model);
    if (data.year) details.push(`(${data.year})`);
    if (data.mileage) details.push(`${data.mileage.toLocaleString()} km`);
    if (data.color) details.push(data.color);
    
    toast.success(
      <div>
        <p className="font-bold">Fahrzeugdaten erfolgreich importiert!</p>
        <p className="text-sm mt-1">{details.join(" • ")}</p>
        {data.features?.length > 0 && (
          <p className="text-xs mt-1 text-green-200">{data.features.length} Ausstattungsmerkmale geladen</p>
        )}
      </div>,
      { duration: 5000 }
    );
  };

  // Ref to store drag counter to avoid stale closure
  const dragCounterRef = useRef(0);

  // Global drag handlers for XML drop - Dropzone erscheint nur wenn Datei gezogen wird
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      dragCounterRef.current++;
      
      // Nur bei Dateien anzeigen
      if (dragCounterRef.current === 1 && e.dataTransfer?.types?.includes('Files')) {
        setShowXMLDropzone(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      dragCounterRef.current--;
      
      // Nur verstecken wenn wirklich alles verlassen
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setShowXMLDropzone(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      // Don't stopPropagation - let the dropzone handle it
    };

    const handleWindowDrop = (e) => {
      // Only prevent default to avoid browser opening the file
      // Don't stopPropagation - let the dropzone component handle the file
      e.preventDefault();
      // Reset counter
      dragCounterRef.current = 0;
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        dragCounterRef.current = 0;
        setShowXMLDropzone(false);
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleWindowDrop);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleWindowDrop);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Print contract function
  const handlePrintContract = useReactToPrint({
    contentRef: contractRef,
    documentTitle: `Kaufvertrag_${contractVehicle?.brand}_${contractVehicle?.model}`,
    onAfterPrint: () => {
      toast.success("Kaufvertrag wurde gedruckt!");
    }
  });

  // Open contract dialog for a vehicle
  const openContractDialog = (vehicle, buyerInfo = null) => {
    setContractVehicle(vehicle);
    if (buyerInfo) {
      setContractBuyer({
        name: buyerInfo.name || "",
        address: buyerInfo.address || "",
        city: buyerInfo.city || "",
        phone: buyerInfo.phone || "",
        email: buyerInfo.email || ""
      });
    } else {
      // Check if there's a contact inquiry for this vehicle
      const vehicleInquiry = contacts.find(c => 
        c.is_vehicle_inquiry && c.vehicle_id === vehicle.id
      );
      if (vehicleInquiry) {
        setContractBuyer({
          name: vehicleInquiry.name || "",
          address: "",
          city: "",
          phone: vehicleInquiry.phone || "",
          email: vehicleInquiry.email || ""
        });
      } else {
        setContractBuyer({ name: "", address: "", city: "", phone: "", email: "" });
      }
    }
    setContractDialog(true);
  };

  // Sell vehicle directly from contact inquiry
  const handleSellFromInquiry = async (contact) => {
    if (!contact.is_vehicle_inquiry || !contact.vehicle_id) {
      toast.error("Keine Fahrzeuganfrage oder Fahrzeug nicht gefunden");
      return;
    }

    // Find the vehicle
    const vehicle = vehicles.find(v => v.id === contact.vehicle_id);
    if (!vehicle) {
      toast.error("Fahrzeug nicht gefunden");
      return;
    }

    if (vehicle.is_sold) {
      toast.error("Fahrzeug ist bereits verkauft");
      return;
    }

    // Confirm sale with custom dialog
    showConfirmDialog(
      "Fahrzeug verkaufen",
      `Möchten Sie "${vehicle.brand} ${vehicle.model}" an "${contact.name}" verkaufen?`,
      async () => {
        try {
          // Mark vehicle as sold
          await axios.put(`${API}/vehicles/${vehicle.id}/sold`, {}, authHeaders);
          toast.success("Fahrzeug als verkauft markiert!");

          // Update local vehicles state
          setVehicles(prev => prev.map(v => 
            v.id === vehicle.id ? { ...v, is_sold: true } : v
          ));

          // Open contract dialog with all data pre-filled
          setContractVehicle({ ...vehicle, is_sold: true });
          setContractBuyer({
            name: contact.name || "",
            address: "",
            city: "",
            phone: contact.phone || "",
            email: contact.email || ""
          });
          setContractDialog(true);

        } catch (error) {
          console.error("Error selling vehicle:", error);
          toast.error("Fehler beim Verkaufen");
        }
        closeConfirmDialog();
      }
    );
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, authHeaders);
    } catch (e) {}
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const resetVehicleForm = () => {
    setVehicleForm({
      brand: "Renault",
      model: "",
      year: new Date().getFullYear(),
      price: "",
      mileage: "",
      fuel_type: "Benzin",
      transmission: "Manuell",
      color: "",
      image_url: "",
      is_new: false,
      features: []
    });
    setEditingVehicle(null);
    setFeatureInput("");
  };

  const openEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      price: vehicle.price.toString(),
      mileage: vehicle.mileage.toString(),
      fuel_type: vehicle.fuel_type,
      transmission: vehicle.transmission,
      color: vehicle.color,
      image_url: vehicle.image_url,
      is_new: vehicle.is_new,
      features: vehicle.features || []
    });
    setVehicleDialog(true);
  };

  const handleSaveVehicle = async () => {
    try {
      // Convert all numeric fields properly, defaulting empty strings to 0
      const data = {
        ...vehicleForm,
        price: parseFloat(vehicleForm.price) || 0,
        mileage: parseInt(vehicleForm.mileage) || 0,
        year: parseInt(vehicleForm.year) || new Date().getFullYear(),
        engine_power_kw: parseInt(vehicleForm.engine_power_kw) || 0,
        engine_power_ps: parseInt(vehicleForm.engine_power_ps) || 0,
        engine_displacement: parseInt(vehicleForm.engine_displacement) || 0,
        doors: parseInt(vehicleForm.doors) || 0,
        seats: parseInt(vehicleForm.seats) || 0,
        co2_emission: parseInt(vehicleForm.co2_emission) || 0,
        fuel_consumption: parseFloat(vehicleForm.fuel_consumption) || 0,
        previous_owners: parseInt(vehicleForm.previous_owners) || 0,
        weight: parseInt(vehicleForm.weight) || 0,
        key_count: parseInt(vehicleForm.key_count) || 2,
      };

      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, data, authHeaders);
        toast.success("Fahrzeug aktualisiert");
      } else {
        await axios.post(`${API}/vehicles`, data, authHeaders);
        toast.success("Fahrzeug hinzugefügt");
      }
      setVehicleDialog(false);
      resetVehicleForm();
      loadData();
    } catch (error) {
      console.error("Save error:", error.response?.data || error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeleteVehicle = async (id) => {
    const vehicle = vehicles.find(v => v.id === id);
    showConfirmDialog(
      "Fahrzeug löschen",
      `Möchten Sie "${vehicle?.brand} ${vehicle?.model}" wirklich löschen?`,
      async () => {
        try {
          await axios.delete(`${API}/vehicles/${id}`, authHeaders);
          toast.success("Fahrzeug gelöscht");
          loadData();
        } catch (error) {
          toast.error("Fehler beim Löschen");
        }
        closeConfirmDialog();
      }
    );
  };

  const handleMarkSold = async (id) => {
    const vehicle = vehicles.find(v => v.id === id);
    showConfirmDialog(
      "Als verkauft markieren",
      `Möchten Sie "${vehicle?.brand} ${vehicle?.model}" als verkauft markieren?`,
      async () => {
        try {
          await axios.put(`${API}/vehicles/${id}/sold`, {}, authHeaders);
          toast.success("Fahrzeug als verkauft markiert");
          loadData();
        } catch (error) {
          toast.error("Fehler beim Markieren");
        }
        closeConfirmDialog();
      }
    );
  };

  const handleDeleteAppointment = async (id) => {
    showConfirmDialog(
      "Termin löschen",
      "Möchten Sie diesen Termin wirklich löschen?",
      async () => {
        try {
          await axios.delete(`${API}/appointments/${id}`, authHeaders);
          toast.success("Termin gelöscht");
          loadData();
        } catch (error) {
          toast.error("Fehler beim Löschen");
        }
        closeConfirmDialog();
      }
    );
  };

  // Review handlers
  const handleApproveReview = async (id, isApproved) => {
    try {
      await axios.put(`${API}/reviews/${id}`, { is_approved: isApproved }, authHeaders);
      toast.success(isApproved ? "Bewertung freigegeben" : "Bewertung versteckt");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleFeatureReview = async (id, isFeatured) => {
    try {
      await axios.put(`${API}/reviews/${id}`, { is_featured: isFeatured }, authHeaders);
      toast.success(isFeatured ? "Als Empfehlung markiert" : "Empfehlung entfernt");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteReview = async (id) => {
    showConfirmDialog(
      "Bewertung löschen",
      "Möchten Sie diese Bewertung wirklich löschen?",
      async () => {
        try {
          await axios.delete(`${API}/reviews/${id}`, authHeaders);
          toast.success("Bewertung gelöscht");
          loadData();
        } catch (error) {
          toast.error("Fehler beim Löschen");
        }
        closeConfirmDialog();
      }
    );
  };

  const handleSubmitReviewResponse = async () => {
    if (!selectedReview || !reviewResponse.trim()) return;
    try {
      await axios.put(`${API}/reviews/${selectedReview.id}`, { admin_response: reviewResponse }, authHeaders);
      toast.success("Antwort gespeichert");
      setReviewResponseDialog(false);
      setSelectedReview(null);
      setReviewResponse("");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const callCustomer = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const openStatusDialog = (appointment, status) => {
    setStatusAppointment(appointment);
    setNewStatus(status);
    setStatusMessage("");
    setStatusDialog(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusAppointment) return;
    setStatusLoading(true);
    
    try {
      await axios.put(
        `${API}/appointments/${statusAppointment.id}/status`,
        { status: newStatus, message: statusMessage },
        authHeaders
      );
      toast.success("Status aktualisiert & E-Mail gesendet");
      setStatusDialog(false);
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setStatusLoading(false);
    }
  };

  const openEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setAppointmentForm({
      preferred_date: appointment.preferred_date,
      preferred_time: appointment.preferred_time,
      service_type: appointment.service_type
    });
    setEditAppointmentDialog(true);
  };

  const handleUpdateAppointment = async () => {
    if (!editingAppointment) return;
    
    try {
      await axios.put(
        `${API}/appointments/${editingAppointment.id}`,
        appointmentForm,
        authHeaders
      );
      toast.success("Termin aktualisiert");
      setEditAppointmentDialog(false);
      loadData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleAppointmentStatus = async (id, status) => {
    const appointment = appointments.find(a => a.id === id);
    if (appointment) {
      openStatusDialog(appointment, status);
    }
  };

  const handleDeleteContact = async (id) => {
    showConfirmDialog(
      "Nachricht löschen",
      "Möchten Sie diese Nachricht wirklich löschen?",
      async () => {
        try {
          await axios.delete(`${API}/contacts/${id}`, authHeaders);
          toast.success("Nachricht gelöscht");
          loadData();
        } catch (error) {
          toast.error("Fehler beim Löschen");
        }
        closeConfirmDialog();
      }
    );
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setVehicleForm(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput("");
    }
  };

  const removeFeature = (index) => {
    setVehicleForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const openChat = (type, item) => {
    // Chat feature removed - placeholder for future implementation
    console.log('Chat feature not implemented', type, item);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800"
    };
    const labels = {
      pending: "Ausstehend",
      confirmed: "Bestätigt",
      cancelled: "Abgesagt",
      completed: "Erledigt"
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  const serviceLabels = {
    inspektion: "Inspektion",
    reparatur: "Reparatur",
    tuev: "TÜV/HU",
    klima: "Klima",
    reifen: "Reifen",
    sonstiges: "Sonstiges"
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#000000] text-white py-4">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EFDF00]  flex items-center justify-center">
              <span className="text-black font-bold ">AT</span>
            </div>
            <div>
              <h1 className="font-bold ">Admin Dashboard</h1>
              <p className="text-xs text-[#888B8D">Auto Thurm</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate("/admin/editor")}
            className="bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold"
            data-testid="admin-open-editor-btn"
          >
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Seiten-Editor
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-white hover:bg-[#1a1a1a"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-0 shadow-none">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100  flex items-center justify-center">
                <Car className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{vehicles.length}</p>
                <p className="text-[#888B8D text-sm">Fahrzeuge</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-none">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100  flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#D9C900" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{appointments.length}</p>
                <p className="text-[#888B8D text-sm">Termine</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-none">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100  flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{contacts.length}</p>
                <p className="text-[#888B8D text-sm">Nachrichten</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="vehicles" data-testid="tab-vehicles">
              <Car className="w-4 h-4 mr-2" />
              Fahrzeuge
            </TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">
              <Calendar className="w-4 h-4 mr-2" />
              Termine
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              <Mail className="w-4 h-4 mr-2" />
              Nachrichten
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <Star className="w-4 h-4 mr-2" />
              Bewertungen
              {reviews.filter(r => !r.is_approved).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 ">
                  {reviews.filter(r => !r.is_approved).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets">
              <FileText className="w-4 h-4 mr-2" />
              Tickets
            </TabsTrigger>
            <TabsTrigger value="aktionen" data-testid="tab-aktionen">
              <Tag className="w-4 h-4 mr-2" />
              Aktionen
              {aktionen.filter(a => a.ist_aktiv).length > 0 && (
                <span className="ml-2 bg-green-500 text-white text-xs px-1.5 py-0.5">
                  {aktionen.filter(a => a.ist_aktiv).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">
              <Bell className="w-4 h-4 mr-2" />
              Erinnerungen
              {reminderStatus?.upcoming_hu_30_days > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-xs px-1.5 py-0.5">
                  {reminderStatus.upcoming_hu_30_days}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <Card className="border-0 shadow-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="">Fahrzeuge verwalten</CardTitle>
                <Dialog open={vehicleDialog} onOpenChange={(open) => {
                  setVehicleDialog(open);
                  if (!open) resetVehicleForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#EFDF00] text-black hover:bg-[#EFDF00" data-testid="add-vehicle-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Fahrzeug hinzufügen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingVehicle ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[70vh] overflow-y-auto pr-2">
                      {/* XML Import Hint */}
                      <div className="bg-[#1a1a1a] border border-[#EFDF00]/30 p-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#EFDF00] flex items-center justify-center flex-shrink-0">
                            <FileUp className="w-5 h-5 text-black" />
                          </div>
                          <div>
                            <p className="text-sm text-white font-bold">Tipp: XML-Import</p>
                            <p className="text-xs text-[#888B8D]">
                              Ziehen Sie eine DAT/VXS Datei in dieses Fenster, um Fahrzeugdaten automatisch zu importieren
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* VIN Display (read-only after import) */}
                      {vehicleForm.vin && (
                        <div className="bg-[#F5F5F5] p-3 mb-4">
                          <Label className="text-xs text-[#888B8D]">Fahrzeug-Identifizierungsnummer (FIN)</Label>
                          <p className="font-mono text-sm">{vehicleForm.vin}</p>
                        </div>
                      )}

                      {/* Basic Info */}
                      <div className="grid grid-cols-2 gap-4 py-4">
                        <div>
                          <Label>Marke *</Label>
                          <Input value={vehicleForm.brand} onChange={(e) => setVehicleForm(p => ({...p, brand: e.target.value}))} placeholder="z.B. Renault" />
                        </div>
                        <div>
                          <Label>Modell *</Label>
                          <Input value={vehicleForm.model} onChange={(e) => setVehicleForm(p => ({...p, model: e.target.value}))} placeholder="z.B. Clio" />
                        </div>
                        <div className="col-span-2">
                          <Label>Börsentyp</Label>
                          <Input value={vehicleForm.boersentyp} onChange={(e) => setVehicleForm(p => ({...p, boersentyp: e.target.value}))} placeholder="z.B. Clio V 1.0 TCe 100 Experience" />
                        </div>
                        <div>
                          <Label>Baujahr *</Label>
                          <Input type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm(p => ({...p, year: e.target.value}))} />
                        </div>
                        <div>
                          <Label>Erstzulassung</Label>
                          <Input type="date" value={vehicleForm.first_registration} onChange={(e) => setVehicleForm(p => ({...p, first_registration: e.target.value}))} />
                        </div>
                        <div>
                          <Label>Preis (€) *</Label>
                          <Input type="number" value={vehicleForm.price} onChange={(e) => setVehicleForm(p => ({...p, price: e.target.value}))} placeholder="z.B. 15990" />
                        </div>
                        <div>
                          <Label>Kilometerstand *</Label>
                          <Input type="number" value={vehicleForm.mileage} onChange={(e) => setVehicleForm(p => ({...p, mileage: e.target.value}))} placeholder="z.B. 35000" />
                        </div>
                      </div>

                      {/* Technical Data */}
                      <div className="border-t pt-4 mt-2">
                        <Label className="font-semibold flex items-center gap-2 mb-3">
                          <Gauge className="w-4 h-4 text-[#EFDF00]" />
                          Technische Daten
                        </Label>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Leistung (kW)</Label>
                            <Input type="number" value={vehicleForm.engine_power_kw} onChange={(e) => {
                              const kw = e.target.value;
                              setVehicleForm(p => ({...p, engine_power_kw: kw, engine_power_ps: kw ? Math.round(kw * 1.36) : ''}));
                            }} placeholder="z.B. 74" />
                          </div>
                          <div>
                            <Label className="text-xs">Leistung (PS)</Label>
                            <Input type="number" value={vehicleForm.engine_power_ps} onChange={(e) => setVehicleForm(p => ({...p, engine_power_ps: e.target.value}))} placeholder="z.B. 100" />
                          </div>
                          <div>
                            <Label className="text-xs">Hubraum (ccm)</Label>
                            <Input type="number" value={vehicleForm.engine_displacement} onChange={(e) => setVehicleForm(p => ({...p, engine_displacement: e.target.value}))} placeholder="z.B. 1197" />
                          </div>
                          <div>
                            <Label className="text-xs">Kraftstoff</Label>
                            <Select value={vehicleForm.fuel_type} onValueChange={(v) => setVehicleForm(p => ({...p, fuel_type: v}))}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Benzin">Benzin</SelectItem>
                                <SelectItem value="Diesel">Diesel</SelectItem>
                                <SelectItem value="Hybrid">Hybrid</SelectItem>
                                <SelectItem value="Plug-in-Hybrid">Plug-in-Hybrid</SelectItem>
                                <SelectItem value="Elektro">Elektro</SelectItem>
                                <SelectItem value="Erdgas">Erdgas (CNG)</SelectItem>
                                <SelectItem value="Autogas">Autogas (LPG)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Getriebe</Label>
                            <Select value={vehicleForm.transmission} onValueChange={(v) => setVehicleForm(p => ({...p, transmission: v}))}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Manuell">Manuell</SelectItem>
                                <SelectItem value="Automatik">Automatik</SelectItem>
                                <SelectItem value="Halbautomatik">Halbautomatik</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Antrieb</Label>
                            <Select value={vehicleForm.drive_type || "Front"} onValueChange={(v) => setVehicleForm(p => ({...p, drive_type: v}))}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Front">Frontantrieb</SelectItem>
                                <SelectItem value="Heck">Heckantrieb</SelectItem>
                                <SelectItem value="Allrad">Allradantrieb</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Vehicle Details */}
                      <div className="border-t pt-4 mt-4">
                        <Label className="font-semibold flex items-center gap-2 mb-3">
                          <Car className="w-4 h-4 text-[#EFDF00]" />
                          Fahrzeugdetails
                        </Label>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Farbe</Label>
                            <Input value={vehicleForm.color} onChange={(e) => setVehicleForm(p => ({...p, color: e.target.value}))} placeholder="z.B. Weiß" />
                          </div>
                          <div>
                            <Label className="text-xs">Fahrzeugtyp</Label>
                            <Select value={vehicleForm.vehicle_type || ""} onValueChange={(v) => setVehicleForm(p => ({...p, vehicle_type: v}))}>
                              <SelectTrigger><SelectValue placeholder="Wählen" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Limousine">Limousine</SelectItem>
                                <SelectItem value="Kombi">Kombi</SelectItem>
                                <SelectItem value="SUV">SUV</SelectItem>
                                <SelectItem value="Kleinwagen">Kleinwagen</SelectItem>
                                <SelectItem value="Kompaktwagen">Kompaktwagen</SelectItem>
                                <SelectItem value="Van">Van</SelectItem>
                                <SelectItem value="Cabrio">Cabrio</SelectItem>
                                <SelectItem value="Coupe">Coupé</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Türen</Label>
                            <Input type="number" value={vehicleForm.doors} onChange={(e) => setVehicleForm(p => ({...p, doors: e.target.value}))} placeholder="5" />
                          </div>
                          <div>
                            <Label className="text-xs">Sitze</Label>
                            <Input type="number" value={vehicleForm.seats} onChange={(e) => setVehicleForm(p => ({...p, seats: e.target.value}))} placeholder="5" />
                          </div>
                        </div>
                      </div>

                      {/* Emissions & Environment */}
                      <div className="border-t pt-4 mt-4">
                        <Label className="font-semibold flex items-center gap-2 mb-3">
                          <Zap className="w-4 h-4 text-[#EFDF00]" />
                          Umwelt & Verbrauch
                        </Label>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Schadstoffklasse</Label>
                            <Select value={vehicleForm.emission_class || ""} onValueChange={(v) => setVehicleForm(p => ({...p, emission_class: v}))}>
                              <SelectTrigger><SelectValue placeholder="Wählen" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Euro 6d">Euro 6d</SelectItem>
                                <SelectItem value="Euro 6d-TEMP">Euro 6d-TEMP</SelectItem>
                                <SelectItem value="Euro 6c">Euro 6c</SelectItem>
                                <SelectItem value="Euro 6b">Euro 6b</SelectItem>
                                <SelectItem value="Euro 6">Euro 6</SelectItem>
                                <SelectItem value="Euro 5">Euro 5</SelectItem>
                                <SelectItem value="Euro 4">Euro 4</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">CO₂ (g/km)</Label>
                            <Input type="number" value={vehicleForm.co2_emission} onChange={(e) => setVehicleForm(p => ({...p, co2_emission: e.target.value}))} placeholder="z.B. 119" />
                          </div>
                          <div>
                            <Label className="text-xs">Verbrauch (l/100km)</Label>
                            <Input type="number" step="0.1" value={vehicleForm.fuel_consumption} onChange={(e) => setVehicleForm(p => ({...p, fuel_consumption: e.target.value}))} placeholder="z.B. 5.2" />
                          </div>
                        </div>
                      </div>

                      {/* Administrative Data */}
                      <div className="border-t pt-4 mt-4">
                        <Label className="font-semibold flex items-center gap-2 mb-3">
                          <Info className="w-4 h-4 text-[#EFDF00]" />
                          Verwaltungsdaten
                        </Label>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">HSN</Label>
                            <Input value={vehicleForm.hsn} onChange={(e) => setVehicleForm(p => ({...p, hsn: e.target.value}))} placeholder="z.B. 3333" maxLength={4} />
                          </div>
                          <div>
                            <Label className="text-xs">TSN</Label>
                            <Input value={vehicleForm.tsn} onChange={(e) => setVehicleForm(p => ({...p, tsn: e.target.value}))} placeholder="z.B. AKL" maxLength={3} />
                          </div>
                          <div>
                            <Label className="text-xs">Kennzeichen</Label>
                            <Input value={vehicleForm.license_plate} onChange={(e) => setVehicleForm(p => ({...p, license_plate: e.target.value.toUpperCase()}))} placeholder="z.B. NEW-TH 123" />
                          </div>
                          <div>
                            <Label className="text-xs">Vorbesitzer</Label>
                            <Input type="number" value={vehicleForm.previous_owners} onChange={(e) => setVehicleForm(p => ({...p, previous_owners: e.target.value}))} placeholder="z.B. 1" />
                          </div>
                          <div>
                            <Label className="text-xs">Nächste HU</Label>
                            <Input type="month" value={vehicleForm.next_inspection} onChange={(e) => setVehicleForm(p => ({...p, next_inspection: e.target.value}))} />
                          </div>
                          <div>
                            <Label className="text-xs">Schlüssel</Label>
                            <Input type="number" value={vehicleForm.key_count} onChange={(e) => setVehicleForm(p => ({...p, key_count: e.target.value}))} placeholder="2" />
                          </div>
                        </div>
                      </div>

                      {/* Image Upload */}
                      <div className="border-t pt-4 mt-4 col-span-2">
                        <Label className="font-semibold">Fahrzeugbild</Label>
                        <div className="mt-2 space-y-3">
                          {/* Image Preview */}
                          {vehicleForm.image_url && (
                            <div className="relative inline-block">
                              <img 
                                src={vehicleForm.image_url} 
                                alt="Fahrzeug Vorschau" 
                                className="w-32 h-24 object-cover  border"
                              />
                              <button
                                type="button"
                                onClick={() => setVehicleForm(p => ({...p, image_url: ""}))}
                                className="absolute -top-2 -right-2 bg-red-500 text-white  p-1 hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          
                          {/* Upload Button */}
                          <div className="flex gap-2">
                            <label className="flex-1">
                              <div className="border-2 border-dashed border-[#D9D9D6]  p-3 text-center cursor-pointer hover:border-[#EFDF00] hover:bg-[#F5F5F5] transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleVehicleImageUpload}
                                  className="hidden"
                                  disabled={vehicleImageUploading}
                                />
                                {vehicleImageUploading ? (
                                  <span className="text-sm text-[#888B8D">Wird hochgeladen...</span>
                                ) : (
                                  <span className="text-sm text-[#888B8D flex items-center justify-center gap-2">
                                    <Upload className="w-4 h-4" />
                                    Bild hochladen
                                  </span>
                                )}
                              </div>
                            </label>
                          </div>
                          
                          {/* Or URL input */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#888B8D">oder URL:</span>
                            <Input 
                              value={vehicleForm.image_url} 
                              onChange={(e) => setVehicleForm(p => ({...p, image_url: e.target.value}))} 
                              placeholder="https://..." 
                              className="text-sm"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2 flex items-center gap-2 border-t pt-4 mt-4">
                        <Checkbox checked={vehicleForm.is_new} onCheckedChange={(c) => setVehicleForm(p => ({...p, is_new: c}))} />
                        <Label>Neuwagen</Label>
                      </div>
                      
                      {/* Ausstattung Section */}
                      <div className="col-span-2 border-t pt-4 mt-4">
                        <Label className="font-semibold flex items-center gap-2 mb-3">
                          <Check className="w-4 h-4 text-[#EFDF00]" />
                          Ausstattung ({vehicleForm.features.length} Merkmale)
                        </Label>
                        
                        {/* Input Row */}
                        <div className="flex gap-2 mb-4">
                          <Input 
                            value={featureInput} 
                            onChange={(e) => setFeatureInput(e.target.value)} 
                            placeholder="z.B. Klimaanlage, Navigation, LED-Scheinwerfer" 
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())} 
                            className="flex-1"
                          />
                          <Button type="button" onClick={addFeature} variant="outline" className="shrink-0">
                            <Plus className="w-4 h-4 mr-1" />
                            Hinzufügen
                          </Button>
                        </div>
                        
                        {/* Features Grid */}
                        {vehicleForm.features.length > 0 ? (
                          <div className="bg-[#F5F5F5] p-4 max-h-[250px] overflow-y-auto">
                            <div className="flex flex-wrap gap-2">
                              {vehicleForm.features.map((f, i) => (
                                <div 
                                  key={i} 
                                  className="flex items-center gap-2 bg-white px-3 py-2 text-sm group hover:bg-red-50 transition-colors cursor-pointer border border-gray-200"
                                  onClick={() => removeFeature(i)}
                                  title="Klicken zum Entfernen"
                                >
                                  <span>{f}</span>
                                  <X className="w-3 h-3 text-gray-400 group-hover:text-red-500 shrink-0" />
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="bg-[#F5F5F5] p-4 text-center text-sm text-[#888B8D]">
                            Noch keine Ausstattungsmerkmale hinzugefügt
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setVehicleDialog(false)}>Abbrechen</Button>
                      <Button onClick={handleSaveVehicle} className="bg-[#000000]">Speichern</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fahrzeug</TableHead>
                      <TableHead>Preis</TableHead>
                      <TableHead>KM</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {v.image_url && <img src={v.image_url} alt="" className="w-16 h-12 object-cover" />}
                            <div>
                              <p className="font-medium">{v.brand} {v.model}</p>
                              <p className="text-sm text-[#888B8D">{v.year} • {v.fuel_type}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{v.price.toLocaleString('de-DE')} €</TableCell>
                        <TableCell>{v.mileage.toLocaleString('de-DE')} km</TableCell>
                        <TableCell>
                          {v.is_sold ? (
                            <Badge className="bg-red-500 text-white">Verkauft</Badge>
                          ) : v.is_new ? (
                            <Badge className="bg-[#EFDF00] text-black">Neu</Badge>
                          ) : (
                            <Badge variant="secondary">Gebraucht</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {v.is_sold && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openContractDialog(v)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Kaufvertrag drucken"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                          {!v.is_sold && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleMarkSold(v.id)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Als verkauft markieren"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEditVehicle(v)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteVehicle(v.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="border-0 shadow-none">
              <CardHeader>
                <CardTitle className="">Termine</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kunde</TableHead>
                      <TableHead>Fahrzeug</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Termin</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {appointments.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{a.customer_name}</p>
                            <p className="text-sm text-[#888B8D">{a.phone}</p>
                            <p className="text-xs text-[#888B8D">{a.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{a.vehicle_info}</p>
                            {a.vin && <p className="text-xs text-[#888B8D">VIN: {a.vin}</p>}
                            {a.mileage && <p className="text-xs text-[#888B8D">KM: {a.mileage}</p>}
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{serviceLabels[a.service_type] || a.service_type}</Badge></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-[#888B8D" />
                            <span>{a.preferred_date}</span>
                            <Clock className="w-4 h-4 text-[#888B8D ml-2" />
                            <span>{a.preferred_time}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(a.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Call */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => callCustomer(a.phone)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Anrufen"
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                            
                            {/* Confirm */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openStatusDialog(a, 'confirmed')}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Bestätigen"
                              disabled={a.status === 'confirmed'}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            
                            {/* Cancel */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openStatusDialog(a, 'cancelled')}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              title="Absagen"
                              disabled={a.status === 'cancelled'}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                            
                            {/* Edit */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditAppointment(a)}
                              title="Bearbeiten"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            
                            {/* Delete */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteAppointment(a.id)} 
                              className="text-red-500"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {appointments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#888B8D py-8">Keine Termine vorhanden</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="border-0 shadow-none">
              <CardHeader>
                <CardTitle className="">Kontaktanfragen</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Typ</TableHead>
                      <TableHead>Absender</TableHead>
                      <TableHead>Betreff</TableHead>
                      <TableHead>Fahrzeug</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((c) => (
                      <TableRow key={c.id} className={c.is_vehicle_inquiry ? "bg-amber-50/50" : ""}>
                        <TableCell>
                          {c.is_vehicle_inquiry ? (
                            <Badge className="bg-[#EFDF00] text-black hover:bg-[#EFDF00]">
                              <Car className="w-3 h-3 mr-1" />
                              Fahrzeug
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Allgemein</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{c.name}</p>
                            <p className="text-sm text-[#888B8D">{c.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{c.subject}</TableCell>
                        <TableCell>
                          {c.is_vehicle_inquiry && c.vehicle_brand ? (
                            <div className="flex items-center gap-2">
                              {c.vehicle_image && (
                                <img 
                                  src={c.vehicle_image} 
                                  alt={`${c.vehicle_brand} ${c.vehicle_model}`}
                                  className="w-10 h-8 object-cover"
                                />
                              )}
                              <span className="text-sm font-medium">
                                {c.vehicle_brand} {c.vehicle_model}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[#888B8D">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-[#888B8D">
                          {new Date(c.created_at).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell>
                          <ContactStatusControl contact={c} headers={authHeaders} onChanged={loadData} />
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {/* Open Detail Button */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setSelectedContact(c);
                              setContactDetailDialog(true);
                            }}
                            className="text-[#888B8D hover:text-[#1a1a1a"
                            title="Öffnen"
                            data-testid={`contact-open-${c.id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          {/* Sell Button for vehicle inquiries */}
                          {c.is_vehicle_inquiry && c.vehicle_id && !vehicles.find(v => v.id === c.vehicle_id)?.is_sold && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleSellFromInquiry(c)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Fahrzeug verkaufen & Vertrag erstellen"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Contract Button for sold vehicle inquiries */}
                          {c.is_vehicle_inquiry && c.vehicle_id && vehicles.find(v => v.id === c.vehicle_id)?.is_sold && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => {
                                const vehicle = vehicles.find(v => v.id === c.vehicle_id);
                                if (vehicle) {
                                  setContractVehicle(vehicle);
                                  setContractBuyer({
                                    name: c.name || "",
                                    address: "",
                                    city: "",
                                    phone: c.phone || "",
                                    email: c.email || ""
                                  });
                                  setContractDialog(true);
                                }
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Kaufvertrag anzeigen"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                          {c.phone && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => callCustomer(c.phone)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Anrufen"
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.id)} className="text-red-500" title="Löschen"><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {contacts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-[#888B8D py-8">Keine Nachrichten vorhanden</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card className="border-0 shadow-none">
              <CardHeader>
                <CardTitle className="">Kundenbewertungen verwalten</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Kunde</TableHead>
                      <TableHead>Bewertung</TableHead>
                      <TableHead>Titel</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((review) => (
                      <TableRow key={review.id} className={!review.is_approved ? "bg-yellow-50" : ""}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {review.is_approved ? (
                              <Badge className="bg-green-500 text-white text-xs">Freigegeben</Badge>
                            ) : (
                              <Badge className="bg-[#EFDF00 text-white text-xs">Ausstehend</Badge>
                            )}
                            {review.is_featured && (
                              <Badge className="bg-[#EFDF00] text-black text-xs">
                                <ThumbsUp className="w-3 h-3 mr-1" />
                                Empfohlen
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{review.customer_name}</p>
                            <p className="text-xs text-[#888B8D">{review.email || "-"}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.rating
                                    ? "fill-[#EFDF00] text-[#EFDF00]"
                                    : "fill-[#D9D9D6] text-[#D9D9D6]"
                                }`}
                              />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{review.title}</p>
                            <p className="text-xs text-[#888B8D truncate max-w-xs">{review.text}</p>
                            {review.admin_response && (
                              <p className="text-xs text-green-600 mt-1">✓ Antwort vorhanden</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-[#888B8D">
                          {new Date(review.created_at).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {/* Approve/Reject */}
                          {!review.is_approved ? (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleApproveReview(review.id, true)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Freigeben"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleApproveReview(review.id, false)}
                              className="text-[#D9C900 hover:text-yellow-700 hover:bg-yellow-50"
                              title="Verstecken"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Feature */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleFeatureReview(review.id, !review.is_featured)}
                            className={review.is_featured ? "text-[#EFDF00]" : "text-[#888B8D"}
                            title={review.is_featured ? "Empfehlung entfernen" : "Als Empfehlung markieren"}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          {/* Reply */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setSelectedReview(review);
                              setReviewResponse(review.admin_response || "");
                              setReviewResponseDialog(true);
                            }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Antworten"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          {/* Delete */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteReview(review.id)} 
                            className="text-red-500"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {reviews.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#888B8D py-8">
                          Keine Bewertungen vorhanden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <Card className="border-0 shadow-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Ticket-Übersicht</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Alle Aktionen werden automatisch protokolliert
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Select 
                    value={selectedTicketCategory} 
                    onValueChange={(val) => {
                      setSelectedTicketCategory(val);
                      setTimeout(() => loadTickets(), 100);
                    }}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Kategorie filtern" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alle">Alle Kategorien</SelectItem>
                      {ticketCategories.map(cat => (
                        <SelectItem key={cat.kategorie} value={cat.kategorie}>
                          {cat.kategorie.replace(/_/g, " ")} ({cat.anzahl})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={exportTickets}
                    className="bg-[#EFDF00] text-black hover:bg-[#D9C900]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    CSV Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {ticketsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-[#EFDF00] border-t-transparent rounded-full"></div>
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Tickets vorhanden</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">Ticket-Nr</TableHead>
                        <TableHead className="w-[180px]">Kategorie</TableHead>
                        <TableHead>Aktion</TableHead>
                        <TableHead className="w-[180px]">Erstellt am</TableHead>
                        <TableHead className="w-[140px]">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tickets.map((ticket) => (
                        <TableRow key={ticket.id}>
                          <TableCell className="font-mono text-sm">{ticket.ticket_nr}</TableCell>
                          <TableCell>
                            <Badge className={
                              ticket.kategorie.includes("ERSTELLT") ? "bg-green-100 text-green-800" :
                              ticket.kategorie.includes("GELOESCHT") || ticket.kategorie.includes("ABGESAGT") ? "bg-red-100 text-red-800" :
                              ticket.kategorie.includes("VERKAUFT") ? "bg-blue-100 text-blue-800" :
                              ticket.kategorie.includes("BEARBEITET") || ticket.kategorie.includes("BESTAETIGT") ? "bg-yellow-100 text-yellow-800" :
                              ticket.kategorie.includes("EINGELAGERT") ? "bg-purple-100 text-purple-800" :
                              ticket.kategorie.includes("AUSGELAGERT") ? "bg-orange-100 text-orange-800" :
                              "bg-gray-100 text-gray-800"
                            }>
                              {ticket.kategorie.replace(/_/g, " ")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ticket.aktion}</p>
                              {ticket.grund && (
                                <p className="text-sm text-gray-500">{ticket.grund}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(ticket.erstellt_am).toLocaleString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {Object.keys(ticket.details || {}).length > 0 && (
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm" title="Details anzeigen">
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-md">
                                    <DialogHeader>
                                      <DialogTitle>Ticket Details</DialogTitle>
                                      <DialogDescription>
                                        {ticket.ticket_nr} - {ticket.kategorie.replace(/_/g, " ")}
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-2">
                                      {Object.entries(ticket.details).map(([key, value]) => (
                                        <div key={key} className="flex justify-between py-2 border-b">
                                          <span className="text-gray-500 capitalize">{key.replace(/_/g, " ")}:</span>
                                          <span className="font-medium">
                                            {Array.isArray(value) ? value.join(", ") : String(value)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                title="Ticket herunterladen"
                                onClick={() => downloadSingleTicket(ticket.id, ticket.ticket_nr)}
                              >
                                <Download className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                title="Ticket löschen"
                                onClick={() => deleteTicket(ticket.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aktionen Tab */}
          <TabsContent value="aktionen">
            <Card className="border-0 shadow-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Aktionen & Promotions</CardTitle>
                  <p className="text-sm text-[#888B8D] mt-1">Verwalten Sie Ihre Werbeaktionen und Sonderangebote</p>
                </div>
                <Dialog open={aktionDialog} onOpenChange={(open) => {
                  setAktionDialog(open);
                  if (!open) resetAktionForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#EFDF00] text-black hover:bg-yellow-400" data-testid="add-aktion-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Neue Aktion
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingAktion ? "Aktion bearbeiten" : "Neue Aktion erstellen"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Titel */}
                      <div>
                        <Label>Titel *</Label>
                        <Input
                          value={aktionForm.titel}
                          onChange={(e) => setAktionForm(prev => ({ ...prev, titel: e.target.value }))}
                          placeholder="z.B. Wintercheck-Aktion"
                          data-testid="aktion-titel-input"
                        />
                      </div>

                      {/* Kurzbeschreibung */}
                      <div>
                        <Label>Kurzbeschreibung (für Kacheln)</Label>
                        <Input
                          value={aktionForm.kurzbeschreibung}
                          onChange={(e) => setAktionForm(prev => ({ ...prev, kurzbeschreibung: e.target.value }))}
                          placeholder="Kurze Vorschau-Beschreibung"
                          data-testid="aktion-kurz-input"
                        />
                      </div>

                      {/* Beschreibung */}
                      <div>
                        <Label>Ausführliche Beschreibung</Label>
                        <Textarea
                          value={aktionForm.beschreibung}
                          onChange={(e) => setAktionForm(prev => ({ ...prev, beschreibung: e.target.value }))}
                          placeholder="Detaillierte Beschreibung der Aktion..."
                          rows={4}
                          data-testid="aktion-beschreibung-input"
                        />
                      </div>

                      {/* Bild */}
                      <div>
                        <Label>Aktionsbild</Label>
                        <div className="flex gap-2 mt-2">
                          <div className="flex-1">
                            <Input
                              value={aktionForm.bild_url}
                              onChange={(e) => setAktionForm(prev => ({ ...prev, bild_url: e.target.value }))}
                              placeholder="Bild-URL oder hochladen"
                            />
                          </div>
                          <label className="cursor-pointer">
                            <Input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleAktionImageUpload}
                            />
                            <Button type="button" variant="outline" asChild>
                              <span><Upload className="w-4 h-4 mr-2" /> Hochladen</span>
                            </Button>
                          </label>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={generateAktionImage}
                            disabled={aktionImageGenerating || !aktionForm.titel}
                            data-testid="generate-ai-image-btn"
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            {aktionImageGenerating ? "Generiere 3 Varianten..." : "3 KI-Bilder"}
                          </Button>
                        </div>
                        {aktionForm.bild_url && (
                          <div className="mt-2 relative">
                            <img 
                              src={aktionForm.bild_url} 
                              alt="Vorschau" 
                              className="w-full h-32 object-cover rounded border"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute top-2 right-2"
                              onClick={() => setAktionForm(prev => ({ ...prev, bild_url: "" }))}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Datum */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Startdatum *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                                data-testid="aktion-start-input"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {aktionForm.start_datum ? (
                                  format(parse(aktionForm.start_datum, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy', { locale: de })
                                ) : (
                                  <span className="text-muted-foreground">Datum wählen</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <CalendarComponent
                                mode="single"
                                selected={aktionForm.start_datum ? parse(aktionForm.start_datum, 'yyyy-MM-dd', new Date()) : undefined}
                                onSelect={(date) => setAktionForm(prev => ({ 
                                  ...prev, 
                                  start_datum: date ? format(date, 'yyyy-MM-dd') : '' 
                                }))}
                                locale={de}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label>Enddatum *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                                data-testid="aktion-end-input"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {aktionForm.end_datum ? (
                                  format(parse(aktionForm.end_datum, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy', { locale: de })
                                ) : (
                                  <span className="text-muted-foreground">Datum wählen</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <CalendarComponent
                                mode="single"
                                selected={aktionForm.end_datum ? parse(aktionForm.end_datum, 'yyyy-MM-dd', new Date()) : undefined}
                                onSelect={(date) => setAktionForm(prev => ({ 
                                  ...prev, 
                                  end_datum: date ? format(date, 'yyyy-MM-dd') : '' 
                                }))}
                                locale={de}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      {/* Priorität */}
                      <div>
                        <Label>Priorität (0-10, höher = prominenter)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          value={aktionForm.prioritaet}
                          onChange={(e) => setAktionForm(prev => ({ ...prev, prioritaet: parseInt(e.target.value) || 0 }))}
                        />
                      </div>

                      {/* Auto-Aktivierung */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="auto_aktivierung"
                          checked={aktionForm.auto_aktivierung}
                          onCheckedChange={(checked) => setAktionForm(prev => ({ ...prev, auto_aktivierung: checked }))}
                        />
                        <Label htmlFor="auto_aktivierung" className="cursor-pointer">
                          Automatische Aktivierung nach Zeitplan
                        </Label>
                      </div>

                      {/* Wiederholung (nur wenn Auto-Aktivierung aktiv) */}
                      {aktionForm.auto_aktivierung && (
                        <div>
                          <Label>Wiederholungsintervall</Label>
                          <Select
                            value={aktionForm.wiederholung}
                            onValueChange={(value) => setAktionForm(prev => ({ ...prev, wiederholung: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Keine Wiederholung</SelectItem>
                              <SelectItem value="daily">Täglich</SelectItem>
                              <SelectItem value="weekly">Wöchentlich</SelectItem>
                              <SelectItem value="monthly">Monatlich</SelectItem>
                              <SelectItem value="yearly">Jährlich</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-[#888B8D] mt-1">
                            Bei Wiederholung wird die Aktion automatisch im gewählten Intervall aktiviert
                          </p>
                        </div>
                      )}

                      {/* Status */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="ist_aktiv"
                          checked={aktionForm.ist_aktiv}
                          onCheckedChange={(checked) => setAktionForm(prev => ({ ...prev, ist_aktiv: checked }))}
                        />
                        <Label htmlFor="ist_aktiv" className="cursor-pointer">
                          Aktion ist aktiv (sofort sichtbar)
                        </Label>
                      </div>

                      {/* Submit */}
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setAktionDialog(false)}>
                          Abbrechen
                        </Button>
                        <Button 
                          className="bg-[#EFDF00] text-black hover:bg-yellow-400"
                          onClick={handleAktionSubmit}
                          data-testid="save-aktion-btn"
                        >
                          {editingAktion ? "Speichern" : "Erstellen"}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Bildvorschau Dialog mit Varianten-Auswahl */}
                <Dialog open={imagePreviewDialog} onOpenChange={setImagePreviewDialog}>
                  <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#EFDF00]" />
                        KI-Bildvarianten - Wählen Sie Ihr Favorit
                      </DialogTitle>
                    </DialogHeader>
                    
                    {imageVarianten.length > 0 && (
                      <div className="space-y-6">
                        {/* Große Vorschau des ausgewählten Bildes */}
                        <div className="relative aspect-video bg-black rounded-lg overflow-hidden border-4 border-[#EFDF00]">
                          <img 
                            src={imageVarianten[selectedImageIndex]} 
                            alt={`Variante ${selectedImageIndex + 1}`}
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute top-3 left-3 bg-[#EFDF00] text-black px-3 py-1 rounded-full text-sm font-bold">
                            Variante {selectedImageIndex + 1} von {imageVarianten.length}
                          </div>
                        </div>

                        {/* Thumbnail-Auswahl */}
                        <div className="grid grid-cols-3 gap-4">
                          {imageVarianten.map((url, index) => (
                            <button
                              key={index}
                              onClick={() => selectImageVariant(index)}
                              className={`relative aspect-video rounded-lg overflow-hidden border-4 transition-all ${
                                index === selectedImageIndex 
                                  ? 'border-[#EFDF00] ring-2 ring-[#EFDF00] ring-offset-2' 
                                  : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              <img 
                                src={url} 
                                alt={`Variante ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                                index === selectedImageIndex ? 'opacity-100' : 'opacity-0 hover:opacity-100'
                              }`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  index === selectedImageIndex ? 'bg-[#EFDF00]' : 'bg-black/50'
                                }`}>
                                  {index === selectedImageIndex ? (
                                    <Check className="w-5 h-5 text-black" />
                                  ) : (
                                    <span className="text-white font-bold">{index + 1}</span>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Aktions-Buttons */}
                        <div className="flex justify-between items-center pt-4 border-t">
                          <p className="text-sm text-[#888B8D]">
                            Klicken Sie auf ein Bild, um es auszuwählen
                          </p>
                          <div className="flex gap-3">
                            <Button 
                              variant="outline" 
                              onClick={() => setImagePreviewDialog(false)}
                            >
                              Abbrechen
                            </Button>
                            <Button 
                              className="bg-[#EFDF00] text-black hover:bg-yellow-400 font-bold"
                              onClick={confirmImageSelection}
                              data-testid="confirm-image-selection"
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Bild übernehmen
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {aktionenLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#EFDF00] border-t-transparent"></div>
                  </div>
                ) : aktionen.length === 0 ? (
                  <div className="text-center py-12 text-[#888B8D]">
                    <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Keine Aktionen vorhanden</p>
                    <p className="text-sm">Erstellen Sie Ihre erste Werbeaktion!</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {aktionen.map((aktion) => (
                      <div 
                        key={aktion.id} 
                        className={`border rounded-lg p-4 ${aktion.ist_aktiv ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                        data-testid={`aktion-row-${aktion.id}`}
                      >
                        <div className="flex gap-4">
                          {/* Bild */}
                          <div className="w-32 h-24 flex-shrink-0 bg-gradient-to-br from-[#EFDF00] to-yellow-500 rounded overflow-hidden">
                            {aktion.bild_url ? (
                              <img src={aktion.bild_url} alt={aktion.titel} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Tag className="w-8 h-8 text-black/30" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h3 className="font-bold text-lg truncate">{aktion.titel}</h3>
                                <p className="text-sm text-[#888B8D] line-clamp-2">
                                  {aktion.kurzbeschreibung || aktion.beschreibung}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {aktion.ist_aktiv ? (
                                  <Badge className="bg-green-500">Aktiv</Badge>
                                ) : (
                                  <Badge variant="secondary">Inaktiv</Badge>
                                )}
                                {aktion.prioritaet >= 5 && (
                                  <Badge className="bg-red-500">Priorität {aktion.prioritaet}</Badge>
                                )}
                              </div>
                            </div>

                            {/* Meta-Info */}
                            <div className="flex items-center gap-4 mt-2 text-xs text-[#888B8D]">
                              <span className="flex items-center gap-1">
                                <CalendarRange className="w-3 h-3" />
                                {formatDateDE(aktion.start_datum)} - {formatDateDE(aktion.end_datum)}
                              </span>
                              {aktion.auto_aktivierung && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {wiederholungLabels[aktion.wiederholung] || "Auto"}
                                </span>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleAktion(aktion.id)}
                                data-testid={`toggle-aktion-${aktion.id}`}
                              >
                                {aktion.ist_aktiv ? (
                                  <><ToggleRight className="w-4 h-4 mr-1" /> Deaktivieren</>
                                ) : (
                                  <><ToggleLeft className="w-4 h-4 mr-1" /> Aktivieren</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => editAktion(aktion)}
                                data-testid={`edit-aktion-${aktion.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" /> Bearbeiten
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => deleteAktion(aktion.id)}
                                data-testid={`delete-aktion-${aktion.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders">
            <Card className="border-0 shadow-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    HU/Inspektions-Erinnerungen
                  </CardTitle>
                  <p className="text-sm text-[#888B8D] mt-1">
                    Automatische E-Mail-Benachrichtigungen für Fahrzeuge aus "Meine Garage"
                  </p>
                </div>
                <Button 
                  className="bg-[#EFDF00] text-black hover:bg-yellow-400"
                  onClick={handleSendReminders}
                  disabled={sendingReminders}
                  data-testid="send-reminders-btn"
                >
                  {sendingReminders ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                      Wird geprüft...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Jetzt prüfen & senden
                    </>
                  )}
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-slate-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#888B8D]">System-Status</p>
                          <p className="text-xl font-bold">
                            {reminderStatus?.task_running ? (
                              <span className="text-green-600 flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                Aktiv
                              </span>
                            ) : (
                              <span className="text-gray-500">Inaktiv</span>
                            )}
                          </p>
                        </div>
                        <Clock className="w-8 h-8 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#888B8D]">Registrierte Fahrzeuge</p>
                          <p className="text-2xl font-bold">{reminderStatus?.total_user_vehicles || 0}</p>
                        </div>
                        <Car className="w-8 h-8 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className={`${reminderStatus?.upcoming_hu_30_days > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#888B8D]">HU fällig (30 Tage)</p>
                          <p className={`text-2xl font-bold ${reminderStatus?.upcoming_hu_30_days > 0 ? 'text-amber-600' : ''}`}>
                            {reminderStatus?.upcoming_hu_30_days || 0}
                          </p>
                        </div>
                        <AlertTriangle className={`w-8 h-8 ${reminderStatus?.upcoming_hu_30_days > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4" />
                    Wie funktionieren die Erinnerungen?
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>Das System prüft täglich um 8:00 Uhr alle Kundenfahrzeuge</li>
                    <li>Erinnerungen werden <strong>30, 14 und 7 Tage</strong> vor Fälligkeit gesendet</li>
                    <li>Kunden erhalten E-Mails mit HU-Datum und Termin-Link</li>
                    <li>Voraussetzung: Kunde hat ein Fahrzeug in "Meine Garage" mit HU-Datum eingetragen</li>
                  </ul>
                </div>

                {/* Upcoming HU List */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Anstehende HU-Termine (nächste 30 Tage)
                  </h3>
                  
                  {reminderLoading ? (
                    <div className="text-center py-8 text-[#888B8D]">
                      <div className="w-8 h-8 border-2 border-[#EFDF00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      Lade Daten...
                    </div>
                  ) : reminderStatus?.upcoming_hu_details?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Kennzeichen</TableHead>
                          <TableHead>Fahrzeug</TableHead>
                          <TableHead>HU-Datum</TableHead>
                          <TableHead>Verbleibend</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reminderStatus.upcoming_hu_details.map((vehicle, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <span className="bg-[#EFDF00] px-2 py-1 font-mono font-bold">
                                {vehicle.kennzeichen || "-"}
                              </span>
                            </TableCell>
                            <TableCell>
                              {vehicle.marke} {vehicle.modell}
                            </TableCell>
                            <TableCell>
                              {new Date(vehicle.datum).toLocaleDateString("de-DE")}
                            </TableCell>
                            <TableCell>
                              <span className={`font-bold ${
                                vehicle.tage_verbleibend <= 7 ? 'text-red-600' :
                                vehicle.tage_verbleibend <= 14 ? 'text-amber-600' :
                                'text-blue-600'
                              }`}>
                                {vehicle.tage_verbleibend} Tage
                              </span>
                            </TableCell>
                            <TableCell>
                              {vehicle.tage_verbleibend <= 7 ? (
                                <Badge className="bg-red-100 text-red-800">Dringend</Badge>
                              ) : vehicle.tage_verbleibend <= 14 ? (
                                <Badge className="bg-amber-100 text-amber-800">Bald fällig</Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-800">Geplant</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-[#888B8D] bg-slate-50 rounded-lg">
                      <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
                      <p className="font-medium">Keine anstehenden HU-Termine</p>
                      <p className="text-sm">Sobald Kunden Fahrzeuge mit HU-Daten registrieren, erscheinen sie hier</p>
                    </div>
                  )}
                </div>

                {/* Refresh Button */}
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    onClick={loadReminderStatus}
                    disabled={reminderLoading}
                  >
                    {reminderLoading ? "Lädt..." : "Aktualisieren"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="border-0 shadow-none">
              <CardHeader>
                <CardTitle className="">Startseiten-Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Hero Image Upload */}
                <div>
                  <Label className="text-lg font-semibold mb-4 block">Hintergrundbild der Startseite</Label>
                  <p className="text-sm text-[#888B8D mb-4">
                    Laden Sie ein Bild hoch, das als Hintergrund für den Hero-Bereich der Startseite angezeigt wird.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Current Image Preview */}
                    <div>
                      <Label className="text-sm text-[#888B8D mb-2 block">Aktuelles Bild</Label>
                      <div className="border  overflow-hidden bg-[#EBEBEB] h-48">
                        {heroImageUrl ? (
                          <img 
                            src={heroImageUrl.startsWith('http') ? heroImageUrl : `${BACKEND_URL}${heroImageUrl}`}
                            alt="Hero Hintergrund"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#888B8D">
                            <div className="text-center">
                              <Image className="w-12 h-12 mx-auto mb-2" />
                              <p className="text-sm">Kein Bild hochgeladen</p>
                              <p className="text-xs">(Standard-Bild wird verwendet)</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Upload Area */}
                    <div>
                      <Label className="text-sm text-[#888B8D mb-2 block">Neues Bild hochladen</Label>
                      <label className="border-2 border-dashed border-[#D9D9D6]  h-48 flex flex-col items-center justify-center cursor-pointer hover:border-[#EFDF00] hover:bg-[#F5F5F5] transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleHeroImageUpload}
                          className="hidden"
                          disabled={heroImageUploading}
                        />
                        {heroImageUploading ? (
                          <div className="text-center">
                            <div className="w-8 h-8 border-2 border-[#EFDF00] border-t-transparent  animate-spin mx-auto mb-2" />
                            <p className="text-sm text-[#888B8D">Wird hochgeladen...</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Upload className="w-10 h-10 text-[#888B8D mx-auto mb-2" />
                            <p className="text-sm font-medium text-[#333333">Klicken zum Hochladen</p>
                            <p className="text-xs text-[#888B8D mt-1">JPG, PNG, WebP (max. 10MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  {heroImageUrl && (
                    <div className="mt-4 flex items-center gap-4">
                      <p className="text-sm text-green-600">✓ Bild aktiv</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                          setHeroImageUrl("");
                          await axios.put(`${API}/settings`, { hero_image_url: "" }, authHeaders);
                          toast.success("Hintergrundbild entfernt");
                        }}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Entfernen
                      </Button>
                    </div>
                  )}
                </div>

                {/* Feature Toggles */}
                <div className="pt-6 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Settings2 className="w-5 h-5" />
                        Website-Funktionen
                      </h3>
                      <p className="text-sm text-[#888B8D]">
                        Aktivieren oder deaktivieren Sie Bereiche Ihrer Website
                      </p>
                    </div>
                    {savingFeatures && (
                      <div className="flex items-center gap-2 text-sm text-[#888B8D]">
                        <div className="w-4 h-4 border-2 border-[#EFDF00] border-t-transparent rounded-full animate-spin" />
                        Speichern...
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fahrzeuge */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_fahrzeuge ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <Car className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Fahrzeuge</p>
                          <p className="text-xs text-[#888B8D]">Fahrzeugangebote anzeigen</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_fahrzeuge}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_fahrzeuge', checked)}
                        data-testid="toggle-fahrzeuge"
                      />
                    </div>

                    {/* Aktionen */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_aktionen ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <Tag className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Aktionen</p>
                          <p className="text-xs text-[#888B8D]">Sonderaktionen & Banner</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_aktionen}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_aktionen', checked)}
                        data-testid="toggle-aktionen"
                      />
                    </div>

                    {/* Termine */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_termine ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Termin buchen</p>
                          <p className="text-xs text-[#888B8D]">Online-Terminbuchung</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_termine}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_termine', checked)}
                        data-testid="toggle-termine"
                      />
                    </div>

                    {/* Bewertungen */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_bewertungen ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <Star className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Bewertungen</p>
                          <p className="text-xs text-[#888B8D]">Kundenbewertungen anzeigen</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_bewertungen}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_bewertungen', checked)}
                        data-testid="toggle-bewertungen"
                      />
                    </div>

                    {/* Kontakt */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_kontakt ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Kontakt</p>
                          <p className="text-xs text-[#888B8D]">Kontaktformular</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_kontakt}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_kontakt', checked)}
                        data-testid="toggle-kontakt"
                      />
                    </div>

                    {/* Reifenlager */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_reifenlager ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <CircleDot className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Reifenlager</p>
                          <p className="text-xs text-[#888B8D]">Reifeneinlagerung für Kunden</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_reifenlager}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_reifenlager', checked)}
                        data-testid="toggle-reifenlager"
                      />
                    </div>

                    {/* Kundenportal */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${featureSettings.feature_kundenportal ? 'bg-[#EFDF00]' : 'bg-gray-300'}`}>
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">Kundenportal</p>
                          <p className="text-xs text-[#888B8D]">Login & Meine Garage</p>
                        </div>
                      </div>
                      <Switch
                        checked={featureSettings.feature_kundenportal}
                        onCheckedChange={(checked) => handleFeatureToggle('feature_kundenportal', checked)}
                        data-testid="toggle-kundenportal"
                      />
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Hinweis:</strong> Deaktivierte Funktionen werden auf der Website ausgeblendet. 
                      Die Daten bleiben erhalten und können jederzeit wieder aktiviert werden.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newStatus === 'confirmed' && '✅ Termin bestätigen'}
              {newStatus === 'cancelled' && '❌ Termin absagen'}
              {newStatus === 'completed' && '🔧 Termin als erledigt markieren'}
              {newStatus === 'pending' && '⏳ Termin auf ausstehend setzen'}
            </DialogTitle>
            <DialogDescription>
              {statusAppointment && (
                <span>
                  Kunde: <strong>{statusAppointment.customer_name}</strong><br />
                  Eine E-Mail wird automatisch an {statusAppointment.email} gesendet.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Nachricht an den Kunden (optional)</Label>
            <Textarea
              value={statusMessage}
              onChange={(e) => setStatusMessage(e.target.value)}
              placeholder="z.B. Bitte bringen Sie Ihren Fahrzeugschein mit..."
              className="mt-2"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>Abbrechen</Button>
            <Button 
              onClick={handleStatusUpdate} 
              disabled={statusLoading}
              className={newStatus === 'cancelled' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}
            >
              <Send className="w-4 h-4 mr-2" />
              {statusLoading ? 'Wird gesendet...' : 'Bestätigen & E-Mail senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={editAppointmentDialog} onOpenChange={setEditAppointmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Termin bearbeiten</DialogTitle>
            {editingAppointment && (
              <DialogDescription>
                Kunde: {editingAppointment.customer_name}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={appointmentForm.preferred_date}
                onChange={(e) => setAppointmentForm(p => ({...p, preferred_date: e.target.value}))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Uhrzeit</Label>
              <Select 
                value={appointmentForm.preferred_time} 
                onValueChange={(v) => setAppointmentForm(p => ({...p, preferred_time: v}))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", 
                    "12:00", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"].map(t => (
                    <SelectItem key={t} value={t}>{t} Uhr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service</Label>
              <Select 
                value={appointmentForm.service_type} 
                onValueChange={(v) => setAppointmentForm(p => ({...p, service_type: v}))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspektion">Inspektion & Wartung</SelectItem>
                  <SelectItem value="reparatur">Reparatur</SelectItem>
                  <SelectItem value="tuev">TÜV / HU</SelectItem>
                  <SelectItem value="klima">Klimaservice</SelectItem>
                  <SelectItem value="reifen">Reifenservice</SelectItem>
                  <SelectItem value="sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAppointmentDialog(false)}>Abbrechen</Button>
            <Button onClick={handleUpdateAppointment} className="bg-[#000000]">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Detail Dialog */}
      <Dialog open={contactDetailDialog} onOpenChange={setContactDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedContact?.is_vehicle_inquiry ? (
                <>
                  <Car className="w-5 h-5 text-[#EFDF00]" />
                  Fahrzeuganfrage
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Kontaktanfrage
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Eingegangen am {selectedContact && new Date(selectedContact.created_at).toLocaleDateString('de-DE', { 
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
              })}
            </DialogDescription>
          </DialogHeader>
          
          {selectedContact && (
            <div className="space-y-6 py-4">
              {/* Vehicle Info (if vehicle inquiry) */}
              {selectedContact.is_vehicle_inquiry && selectedContact.vehicle_brand && (
                <div className="bg-gradient-to-r from-[#000000] to-#1a1a1a  p-4 text-white">
                  <p className="text-[#EFDF00] text-sm font-semibold mb-3">Angefragtes Fahrzeug</p>
                  <div className="flex gap-4">
                    {selectedContact.vehicle_image && (
                      <img 
                        src={selectedContact.vehicle_image} 
                        alt={`${selectedContact.vehicle_brand} ${selectedContact.vehicle_model}`}
                        className="w-32 h-24 object-cover "
                      />
                    )}
                    <div>
                      <p className="text-2xl font-bold">{selectedContact.vehicle_brand} {selectedContact.vehicle_model}</p>
                      <p className="text-[#D9D9D6]">Baujahr {selectedContact.vehicle_year}</p>
                      <p className="text-[#EFDF00] text-xl font-bold mt-2">
                        {selectedContact.vehicle_price?.toLocaleString('de-DE')} €
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#888B8D text-xs">Name</Label>
                  <p className="font-medium text-black">{selectedContact.name}</p>
                </div>
                <div>
                  <Label className="text-[#888B8D text-xs">Betreff</Label>
                  <p className="font-medium text-black">{selectedContact.subject}</p>
                </div>
                <div>
                  <Label className="text-[#888B8D text-xs">E-Mail</Label>
                  <a href={`mailto:${selectedContact.email}`} className="font-medium text-blue-600 hover:underline">
                    {selectedContact.email}
                  </a>
                </div>
                <div>
                  <Label className="text-[#888B8D text-xs">Telefon</Label>
                  {selectedContact.phone ? (
                    <a href={`tel:${selectedContact.phone}`} className="font-medium text-blue-600 hover:underline">
                      {selectedContact.phone}
                    </a>
                  ) : (
                    <p className="text-[#888B8D">Nicht angegeben</p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label className="text-[#888B8D text-xs">Nachricht</Label>
                <div className="mt-2 p-4 bg-[#F5F5F5]  border whitespace-pre-wrap">
                  {selectedContact.message}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 flex-wrap">
            {/* Sell Button for vehicle inquiries */}
            {selectedContact?.is_vehicle_inquiry && selectedContact?.vehicle_id && 
             !vehicles.find(v => v.id === selectedContact.vehicle_id)?.is_sold && (
              <Button 
                onClick={() => {
                  setContactDetailDialog(false);
                  handleSellFromInquiry(selectedContact);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Fahrzeug verkaufen
              </Button>
            )}
            {/* Contract Button for already sold vehicles */}
            {selectedContact?.is_vehicle_inquiry && selectedContact?.vehicle_id && 
             vehicles.find(v => v.id === selectedContact.vehicle_id)?.is_sold && (
              <Button 
                onClick={() => {
                  const vehicle = vehicles.find(v => v.id === selectedContact.vehicle_id);
                  if (vehicle) {
                    setContactDetailDialog(false);
                    setContractVehicle(vehicle);
                    setContractBuyer({
                      name: selectedContact.name || "",
                      address: "",
                      city: "",
                      phone: selectedContact.phone || "",
                      email: selectedContact.email || ""
                    });
                    setContractDialog(true);
                  }
                }}
                className="bg-[#EFDF00] hover:bg-[#EFDF00 text-black"
              >
                <FileText className="w-4 h-4 mr-2" />
                Kaufvertrag
              </Button>
            )}
            {selectedContact?.phone && (
              <Button 
                variant="outline" 
                onClick={() => callCustomer(selectedContact.phone)}
                className="text-blue-600"
              >
                <Phone className="w-4 h-4 mr-2" />
                Anrufen
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={() => window.location.href = `mailto:${selectedContact?.email}`}
            >
              <Mail className="w-4 h-4 mr-2" />
              E-Mail senden
            </Button>
            <Button onClick={() => setContactDetailDialog(false)} className="bg-[#000000]">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Contract Dialog */}
      <Dialog open={contractDialog} onOpenChange={setContractDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#EFDF00]" />
              Kaufvertrag erstellen
            </DialogTitle>
            <DialogDescription>
              Fahrzeug: {contractVehicle?.brand} {contractVehicle?.model} ({contractVehicle?.year})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Buyer Information Form */}
            <div className="border  p-4 bg-[#F5F5F5]">
              <h3 className="font-semibold mb-3">Käuferdaten eingeben</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input 
                    value={contractBuyer.name}
                    onChange={(e) => setContractBuyer(p => ({...p, name: e.target.value}))}
                    placeholder="Max Mustermann"
                  />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input 
                    value={contractBuyer.email}
                    onChange={(e) => setContractBuyer(p => ({...p, email: e.target.value}))}
                    placeholder="max@beispiel.de"
                  />
                </div>
                <div>
                  <Label>Adresse</Label>
                  <Input 
                    value={contractBuyer.address}
                    onChange={(e) => setContractBuyer(p => ({...p, address: e.target.value}))}
                    placeholder="Musterstraße 123"
                  />
                </div>
                <div>
                  <Label>PLZ / Ort</Label>
                  <Input 
                    value={contractBuyer.city}
                    onChange={(e) => setContractBuyer(p => ({...p, city: e.target.value}))}
                    placeholder="12345 Musterstadt"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input 
                    value={contractBuyer.phone}
                    onChange={(e) => setContractBuyer(p => ({...p, phone: e.target.value}))}
                    placeholder="+49 123 456789"
                  />
                </div>
                <div>
                  <Label>Ausweis-Nr.</Label>
                  <Input 
                    value={contractBuyer.idNumber}
                    onChange={(e) => setContractBuyer(p => ({...p, idNumber: e.target.value}))}
                    placeholder="Personalausweis-Nr."
                  />
                </div>
              </div>
            </div>

            {/* Contract Options */}
            <div className="border  p-4 bg-[#F5F5F5]">
              <h3 className="font-semibold mb-4">Vertragsoptionen</h3>
              
              {/* Warranty Options */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Garantie</Label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.warranty12Months}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, 
                        warranty12Months: checked, 
                        warranty24Months: checked ? false : p.warranty24Months
                      }))}
                    />
                    <span className="text-sm">12 Monate Garantie (+349 €)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.warranty24Months}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, 
                        warranty24Months: checked, 
                        warranty12Months: checked ? false : p.warranty12Months
                      }))}
                    />
                    <span className="text-sm">24 Monate Garantie (+599 €)</span>
                  </label>
                </div>
              </div>

              {/* HU/AU */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={contractOptions.newInspection}
                    onCheckedChange={(checked) => setContractOptions(p => ({...p, newInspection: checked}))}
                  />
                  <span className="text-sm">Neue HU/AU-Prüfung (+89 €)</span>
                </label>
              </div>

              {/* Winter Tires */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={contractOptions.winterTires}
                    onCheckedChange={(checked) => setContractOptions(p => ({...p, winterTires: checked}))}
                  />
                  <span className="text-sm">Winterreifen/Kompletträder</span>
                </label>
                {contractOptions.winterTires && (
                  <Input 
                    type="number"
                    value={contractOptions.winterTiresPrice}
                    onChange={(e) => setContractOptions(p => ({...p, winterTiresPrice: parseFloat(e.target.value) || 0}))}
                    placeholder="Preis"
                    className="mt-2 w-32"
                  />
                )}
              </div>

              {/* Trade-in */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={contractOptions.tradeIn}
                    onCheckedChange={(checked) => setContractOptions(p => ({...p, tradeIn: checked}))}
                  />
                  <span className="text-sm">Inzahlungnahme Altfahrzeug</span>
                </label>
                {contractOptions.tradeIn && (
                  <div className="mt-2 flex gap-2">
                    <Input 
                      value={contractOptions.tradeInVehicle}
                      onChange={(e) => setContractOptions(p => ({...p, tradeInVehicle: e.target.value}))}
                      placeholder="Fahrzeug (z.B. VW Golf 2018)"
                      className="flex-1"
                    />
                    <Input 
                      type="number"
                      value={contractOptions.tradeInValue}
                      onChange={(e) => setContractOptions(p => ({...p, tradeInValue: parseFloat(e.target.value) || 0}))}
                      placeholder="Wert €"
                      className="w-28"
                    />
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Zahlungsart</Label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.paymentCash}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, paymentCash: checked, paymentTransfer: false, paymentFinancing: false
                      }))}
                    />
                    <span className="text-sm">Barzahlung</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.paymentTransfer}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, paymentTransfer: checked, paymentCash: false, paymentFinancing: false
                      }))}
                    />
                    <span className="text-sm">Überweisung</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.paymentFinancing}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, paymentFinancing: checked, paymentCash: false, paymentTransfer: false
                      }))}
                    />
                    <span className="text-sm">Finanzierung</span>
                  </label>
                </div>
                {contractOptions.paymentFinancing && (
                  <Input 
                    value={contractOptions.financingBank}
                    onChange={(e) => setContractOptions(p => ({...p, financingBank: e.target.value}))}
                    placeholder="Bank/Kreditinstitut"
                    className="mt-2"
                  />
                )}
              </div>

              {/* Vehicle Condition */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Fahrzeugzustand</Label>
                <div className="flex flex-wrap gap-4 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.accidentFree}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, accidentFree: checked, accidentRepaired: false, accidentUnknown: false
                      }))}
                    />
                    <span className="text-sm">Unfallfrei</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.accidentRepaired}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, accidentRepaired: checked, accidentFree: false, accidentUnknown: false
                      }))}
                    />
                    <span className="text-sm">Unfallschaden repariert</span>
                  </label>
                </div>
                <Textarea 
                  value={contractOptions.knownDefects}
                  onChange={(e) => setContractOptions(p => ({...p, knownDefects: e.target.value}))}
                  placeholder="Bekannte Mängel (optional)"
                  className="mt-2"
                  rows={2}
                />
              </div>

              {/* Buyer Type */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Käufertyp</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.buyerPrivate}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, buyerPrivate: checked, buyerBusiness: !checked
                      }))}
                    />
                    <span className="text-sm">Privatkäufer (12 Mon. Gewährleistung)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.buyerBusiness}
                      onCheckedChange={(checked) => setContractOptions(p => ({
                        ...p, buyerBusiness: checked, buyerPrivate: !checked
                      }))}
                    />
                    <span className="text-sm">Gewerblich (Gewährleistungsausschluss)</span>
                  </label>
                </div>
              </div>

              {/* Included Items */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Im Kaufpreis enthalten</Label>
                <div className="grid grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.includeServiceBook}
                      onCheckedChange={(checked) => setContractOptions(p => ({...p, includeServiceBook: checked}))}
                    />
                    <span className="text-sm">Scheckheft</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.includeManual}
                      onCheckedChange={(checked) => setContractOptions(p => ({...p, includeManual: checked}))}
                    />
                    <span className="text-sm">Betriebsanleitung</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.includeSpareWheel}
                      onCheckedChange={(checked) => setContractOptions(p => ({...p, includeSpareWheel: checked}))}
                    />
                    <span className="text-sm">Ersatzrad</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.includeFirstAid}
                      onCheckedChange={(checked) => setContractOptions(p => ({...p, includeFirstAid: checked}))}
                    />
                    <span className="text-sm">Verbandskasten</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.includeWarningTriangle}
                      onCheckedChange={(checked) => setContractOptions(p => ({...p, includeWarningTriangle: checked}))}
                    />
                    <span className="text-sm">Warndreieck</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={contractOptions.includeWinterTires}
                      onCheckedChange={(checked) => setContractOptions(p => ({...p, includeWinterTires: checked}))}
                    />
                    <span className="text-sm">Winterreifen</span>
                  </label>
                </div>
              </div>

              {/* Additional Agreements */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Sonstige Vereinbarungen</Label>
                <Textarea 
                  value={contractOptions.additionalAgreements}
                  onChange={(e) => setContractOptions(p => ({...p, additionalAgreements: e.target.value}))}
                  placeholder="z.B. Anzahlung, besondere Absprachen..."
                  rows={2}
                />
              </div>
            </div>

            {/* Price Summary */}
            <div className="border  p-4 bg-green-50">
              <h3 className="font-semibold mb-2">Preisübersicht</h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Fahrzeugpreis:</span>
                  <span>{(contractVehicle?.price || 0).toLocaleString('de-DE')} €</span>
                </div>
                {contractOptions.warranty12Months && (
                  <div className="flex justify-between text-green-700">
                    <span>+ 12 Monate Garantie:</span>
                    <span>349 €</span>
                  </div>
                )}
                {contractOptions.warranty24Months && (
                  <div className="flex justify-between text-green-700">
                    <span>+ 24 Monate Garantie:</span>
                    <span>599 €</span>
                  </div>
                )}
                {contractOptions.newInspection && (
                  <div className="flex justify-between text-green-700">
                    <span>+ Neue HU/AU:</span>
                    <span>89 €</span>
                  </div>
                )}
                {contractOptions.winterTires && contractOptions.winterTiresPrice > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>+ Winterreifen:</span>
                    <span>{contractOptions.winterTiresPrice.toLocaleString('de-DE')} €</span>
                  </div>
                )}
                {contractOptions.tradeIn && contractOptions.tradeInValue > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>- Inzahlungnahme:</span>
                    <span>-{contractOptions.tradeInValue.toLocaleString('de-DE')} €</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                  <span>Gesamtpreis:</span>
                  <span>
                    {(
                      (contractVehicle?.price || 0) +
                      (contractOptions.warranty24Months ? 599 : (contractOptions.warranty12Months ? 349 : 0)) +
                      (contractOptions.newInspection ? 89 : 0) +
                      (contractOptions.winterTires ? contractOptions.winterTiresPrice : 0) -
                      (contractOptions.tradeIn ? contractOptions.tradeInValue : 0)
                    ).toLocaleString('de-DE')} €
                  </span>
                </div>
              </div>
            </div>

            {/* Contract Preview */}
            <div className="border  overflow-hidden">
              <div className="bg-[#EBEBEB] px-4 py-2 flex items-center justify-between">
                <span className="text-sm font-medium">Vertragsvorschau</span>
                <Button 
                  onClick={handlePrintContract}
                  className="bg-[#000000]"
                  disabled={!contractBuyer.name}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Drucken
                </Button>
              </div>
              <div className="max-h-[300px] overflow-y-auto bg-white p-2" style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%' }}>
                <PurchaseContract 
                  ref={contractRef}
                  vehicle={contractVehicle}
                  buyer={contractBuyer}
                  options={contractOptions}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setContractDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handlePrintContract}
              className="bg-[#EFDF00] text-black hover:bg-[#EFDF00"
              disabled={!contractBuyer.name}
            >
              <Printer className="w-4 h-4 mr-2" />
              Kaufvertrag drucken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Response Dialog */}
      <Dialog open={reviewResponseDialog} onOpenChange={setReviewResponseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Auf Bewertung antworten</DialogTitle>
            <DialogDescription>
              Bewertung von {selectedReview?.customer_name}: "{selectedReview?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-[#F5F5F5] ">
              <div className="flex gap-0.5 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= (selectedReview?.rating || 0)
                        ? "fill-[#EFDF00] text-[#EFDF00]"
                        : "fill-[#D9D9D6] text-[#D9D9D6]"
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm text-[#888B8D">{selectedReview?.text}</p>
            </div>
            
            <Label>Ihre Antwort</Label>
            <Textarea 
              value={reviewResponse}
              onChange={(e) => setReviewResponse(e.target.value)}
              placeholder="Vielen Dank für Ihre Bewertung..."
              rows={4}
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewResponseDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSubmitReviewResponse}
              className="bg-[#000000]"
              disabled={!reviewResponse.trim()}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Antwort speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog (replaces window.confirm) */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {confirmDialog.title}
            </DialogTitle>
            <DialogDescription className="text-gray-600 pt-2">
              {confirmDialog.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={closeConfirmDialog}
              data-testid="confirm-dialog-cancel"
            >
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDialog.onConfirm}
              data-testid="confirm-dialog-confirm"
            >
              Bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* XML Dropzone - erscheint NUR wenn Datei in den Browser gezogen wird */}
      <VehicleXMLDropzone 
        isVisible={showXMLDropzone}
        onVehicleData={handleXMLVehicleData}
        onClose={() => setShowXMLDropzone(false)}
      />
    </div>
  );
};

export default AdminDashboard;
